import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type {
  Query,
  Mutation,
  UpsertTicketInput,
  UpsertListInput,
  UpsertAgentInput,
  UpdateSheetInput,
  CreateTemplateInput,
  ApplyTemplateInput,
  UpsertOfferContactPayload,
  UpsertExpenseCategoryInput,
  UpsertExpenseInput,
  LockSheetInput,
  GeneratePerformanceDatesInput,
} from '@/generated/graphql';
import { ensureNumbers } from '@/helpers/ensureNumber';
import * as OfferSheetTicket from '@/types/OfferSheetTicket';
import * as User from '@/types/User';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

export interface SheetVersion {
  id: number;
  version: number;
  versionName?: string;
}

export interface OfferSheet {
  id: number;
  name: string;
  versions?: SheetVersion[];
  listCount: number;
  lists: Array<List>;
  merch: string;
  offerDate: string;
  offerExpirationDate: string;
  promoterName: string;
  startTime: string;
  summary?: Array<Summary>;
  support: string;
  supportGuarantee: number;
  ticketCount: number;
  tickets?: OfferSheetTicket.OfferSheetTicket[];
  agent: Agent;
  artistName: string;
  attendeeCount: number;
  billing: string;
  contacts: Array<Contact>;
  endTime: string;
  dealArtistGuarantee: number;
  dealGborPercentage: number;
  dealType: string;
  eventGroupId: number;
  exclusivity: string;
  expenseCategories: Array<ExpenseCategory>;
  venueAddress: string;
  venueLogo: string;
  venueName: string;
  venueTimezone: string;
  doorTime: string;
  minimumAge: number;
  note: string;
  performanceDatesDescription: string;
  promoterProfitPercentage: number;
  taxPercentage: number;
  htmlContent: string;
  pdfDownloadUrl: string;
  headlinerGuarantee: number;
  lockedAt?: string;
  lockedBy?: User.User;
}

// const NULLABLE_NUMBER_FIELDS = [
//   'dealArtistGuarantee',
//   'dealGborPercentage',
//   'supportGuarantee',
//   'taxPercentage',
//   'promoterProfitPercentage',
// ];

interface Summary {
  artistPotential: number;
  breakeven: number;
  fixedExpenses: number;
  gbor: number;
  netPotential: number;
  overage: number;
  promoterPotential: number;
  splitPoint: number;
  variableExpenses: number;
}

interface Agent {
  id: number;
  agencyName: string;
  email: string;
  name: string;
}

interface Contact {
  id: number;
  offerSheetId: number;
  email: string;
  role: string;
  name: string;
}

interface ExpenseCategory {
  id: number;
  name: string;
  totalExpenses: number;
  editable: boolean;
  expenses: Expense[];
}

interface Expense {
  id: number;
  name: string;
  calculationType: string;
  calculationValue: number;
  amount: number;
  editable: boolean;
}

interface List {
  id: number;
  gross: number;
  name: string;
  listType: string;
  projected: number;
}

interface OfferSheetState {
  offerSheet?: OfferSheet;
  offerSheetLoading: boolean;
  agentLoading: boolean;
  contactsLoading: boolean;
  expensesLoading: boolean;
  listLoading: boolean;
  ticketsLoading: boolean;
  templateLoading: boolean;
  summaryReloading: boolean;
  templates: OfferSheetTemplate[];
}

interface OfferSheetTemplate {
  id: number;
  templateName: string;
}

const OFFER_SHEET_QUERY = gql`
  query offerSheet($id: Int!) {
    offerSheet(id: $id) {
      versions {
        id
        version
        versionName
      }
      htmlContent
      pdfDownloadUrl
      lockedAt
      lockedBy {
        name
      }
      agent {
        agencyName
        email
        id
        name
      }
      artistName
      attendeeCount
      billing
      doorTime
      minimumAge
      contacts {
        createdAt
        email
        id
        name
        role
        updatedAt
      }
      endTime
      dealArtistGuarantee
      dealGborPercentage
      dealType
      eventGroupId
      exclusivity
      id
      listCount
      performanceDatesDescription
      lists {
        id
        name
        projected
        gross
        listType
      }
      merch
      name
      note
      offerDate
      offerExpirationDate
      promoterName
      startTime
      summary {
        artistPotential
        breakeven
        fixedExpenses
        gbor
        netPotential
        overage
        promoterPotential
        splitPoint
        variableExpenses
      }
      support
      supportGuarantee
      ticketCount
      tickets {
        gross
        id
        name
        price
        projected
        section
      }
      venueAddress
      venueLogo
      venueName
      venueTimezone
      taxPercentage
      promoterProfitPercentage
      expenseCategories {
        id
        name
        totalExpenses
        editable
        expenses {
          id
          name
          amount
          calculationType
          calculationValue
          editable
        }
      }
    }
  }
`;

const OFFER_SHEET_SUMMARY_QUERY = gql`
  query offerSheet($id: Int!) {
    offerSheet(id: $id) {
      htmlContent
      summary {
        artistPotential
        breakeven
        fixedExpenses
        gbor
        netPotential
        overage
        promoterPotential
        splitPoint
        variableExpenses
      }
    }
  }
`;

const OFFER_SHEET_TEMPLATES_QUERY = gql`
  query {
    templates {
      id
      templateName
    }
  }
`;

export interface SaveOfferSheetParams {
  id: number;
  merch: string;
  offerExpirationDate: string;
  dealType: string;
  dealArtistGuarantee: number;
  dealGborPercentage: number;
  promoterName: string;
  billing: string;
  support: string;
  supportGuarantee: number;
  exclusivity: string;
  taxPercentage: number;
}

const OFFER_SHEET_MUTATION = gql`
  mutation updateSheetMutation($input: UpdateSheetInput!) {
    offerSheet {
      updateSheet(input: $input) {
        clientMutationId
        sheet {
          id
          merch
          offerExpirationDate
          dealType
          dealArtistGuarantee
          dealGborPercentage
          promoterName
          billing
          support
          supportGuarantee
          exclusivity
          taxPercentage
          promoterProfitPercentage
          billing
          minimumAge
          doorTime
          startTime
          endTime
          name
          note
        }
      }
    }
  }
`;

const CREATE_VERSION_MUTATION = gql`
  mutation createSheetVersion($input: CreateSheetVersionInput!) {
    offerSheet {
      createVersion(input: $input) {
        clientMutationId
        version {
          id
          version
          versionName
        }
      }
    }
  }
`;

export interface CreateTemplateParams {
  offerSheetId: number;
  templateName: string;
  clientMutationId: string;
}

const CREATE_TEMPLATE_MUTATION = gql`
  mutation createTemplateMutation($input: CreateTemplateInput!) {
    offerSheet {
      createTemplate(input: $input) {
        clientMutationId
        errors
        template {
          id
          templateName
        }
      }
    }
  }
`;

export interface ApplyTemplateParams {
  offerSheetId: number;
  templateId: string;
  clientMutationId: string;
}

const APPLY_TEMPLATE_MUTATION = gql`
  mutation applyTemplateMutation($input: ApplyTemplateInput!) {
    offerSheet {
      applyTemplate(input: $input) {
        clientMutationId
        errors
        sheet {
          agent {
            agencyName
            email
            id
            name
          }
          artistName
          attendeeCount
          billing
          doorTime
          minimumAge
          contacts {
            createdAt
            email
            id
            name
            role
            updatedAt
          }
          endTime
          dealArtistGuarantee
          dealGborPercentage
          dealType
          eventGroupId
          exclusivity
          id
          listCount
          performanceDatesDescription
          lists {
            id
            name
            projected
            gross
            listType
          }
          merch
          name
          note
          offerDate
          offerExpirationDate
          promoterName
          startTime
          summary {
            artistPotential
            breakeven
            fixedExpenses
            gbor
            netPotential
            overage
            promoterPotential
            splitPoint
            variableExpenses
          }
          support
          supportGuarantee
          ticketCount
          tickets {
            gross
            id
            name
            price
            projected
            section
          }
          venueAddress
          venueLogo
          venueName
          taxPercentage
          promoterProfitPercentage
          expenseCategories {
            id
            name
            totalExpenses
            editable
            expenses {
              id
              name
              amount
              calculationType
              calculationValue
              editable
            }
          }
        }
      }
    }
  }
`;

export interface SaveAgentParams {
  id: number;
  offerSheetId: number;
  name: string;
  email: string;
  agencyName: string;
}

const AGENT_MUTATION = gql`
  mutation upsertAgentMutation($input: UpsertAgentInput!) {
    offerSheet {
      upsertAgent(input: $input) {
        clientMutationId
        errors
        sheet {
          agent {
            agencyName
            email
            id
            name
          }
        }
      }
    }
  }
`;

export interface SaveContactParams {
  id: number;
  offerSheetId: number;
  role: string;
  name: string;
  email: string;
}

const CONTACT_MUTATION = gql`
  mutation upsertContactMutation($input: UpsertOfferContactInput!) {
    offerSheet {
      upsertOfferContact(input: $input) {
        clientMutationId
        contacts {
          createdAt
          email
          id
          name
          role
          updatedAt
        }
      }
    }
  }
`;

const DELETE_CONTACT_MUTATION = gql`
  mutation deleteContactMutation($input: DeleteOfferContactInput!) {
    offerSheet {
      deleteOfferContact(input: $input) {
        clientMutationId
        contacts {
          createdAt
          email
          id
          name
          role
          updatedAt
        }
      }
    }
  }
`;

const EXPENSE_CATEGORY_MUTATION = gql`
  mutation upsertExpenseCategoryMutation($input: UpsertExpenseCategoryInput!) {
    offerSheet {
      upsertExpenseCategory(input: $input) {
        clientMutationId
        sheet {
          expenseCategories {
            id
            name
            totalExpenses
            editable
            expenses {
              id
              name
              amount
              calculationType
              calculationValue
              editable
            }
          }
        }
        errors
        expenseCategory {
          id
          name
          totalExpenses
          editable
          expenses {
            id
            name
            amount
            calculationType
            calculationValue
            editable
          }
        }
      }
    }
  }
`;

const DELETE_EXPENSE_CATEGORY_MUTATION = gql`
  mutation deleteExpenseCategoryMutation($input: DeleteExpenseCategoryInput!) {
    offerSheet {
      deleteExpenseCategory(input: $input) {
        clientMutationId
        sheet {
          expenseCategories {
            id
            name
            totalExpenses
            editable
            expenses {
              id
              name
              amount
              calculationType
              calculationValue
              editable
            }
          }
        }
        errors
      }
    }
  }
`;

const EXPENSE_MUTATION = gql`
  mutation upsertExpenseMutation($input: UpsertExpenseInput!) {
    offerSheet {
      upsertExpense(input: $input) {
        clientMutationId
        sheet {
          expenseCategories {
            id
            name
            totalExpenses
            editable
            expenses {
              id
              name
              amount
              calculationType
              calculationValue
              editable
            }
          }
        }
        errors
        expense {
          id
          name
          amount
          calculationType
          calculationValue
          editable
        }
      }
    }
  }
`;

const DELETE_EXPENSE_MUTATION = gql`
  mutation deleteExpenseMutation($input: DeleteExpenseInput!) {
    offerSheet {
      deleteExpense(input: $input) {
        clientMutationId
        sheet {
          expenseCategories {
            id
            name
            totalExpenses
            editable
            expenses {
              id
              name
              amount
              calculationType
              calculationValue
              editable
            }
          }
        }
        errors
      }
    }
  }
`;

const LIST_MUTATION = gql`
  mutation upsertListMutation($input: UpsertListInput!) {
    offerSheet {
      upsertList(input: $input) {
        clientMutationId
        sheet {
          id
          listCount
          lists {
            id
            name
            projected
            gross
            listType
          }
        }
        list {
          id
          name
          projected
          gross
          listType
        }
      }
    }
  }
`;

const TICKET_MUTATION = gql`
  mutation upsertTicketMutation($input: UpsertTicketInput!) {
    offerSheet {
      upsertTicket(input: $input) {
        clientMutationId
        sheet {
          ticketCount
          tickets {
            gross
            id
            name
            price
            projected
            section
          }
        }
        ticket {
          gross
          id
          name
          price
          projected
          section
        }
      }
    }
  }
`;

const DELETE_TICKET_MUTATION = gql`
  mutation deleteTicketMutation($input: DeleteTicketInput!) {
    offerSheet {
      deleteTicket(input: $input) {
        clientMutationId
        sheet {
          ticketCount
          tickets {
            gross
            id
            name
            price
            projected
            section
          }
        }
        errors
      }
    }
  }
`;

const DELETE_LIST_MUTATION = gql`
  mutation deleteListMutation($input: DeleteListInput!) {
    offerSheet {
      deleteList(input: $input) {
        clientMutationId
        sheet {
          listCount
          lists {
            id
            name
            projected
            gross
            listType
          }
        }
        errors
      }
    }
  }
`;

const LOCK_SHEET_MUTATION = gql`
  mutation lockSheetMutation($input: LockSheetInput!) {
    offerSheet {
      lockSheet(input: $input) {
        clientMutationId
        sheet {
          lockedAt
          lockedBy {
            name
          }
          pdfDownloadUrl
        }
      }
    }
  }
`;

const GENERATE_PERFORMANCE_DATES_MUTATION = gql`
  mutation generatePerformanceDatesMutation($input: GeneratePerformanceDatesInput!) {
    offerSheet {
      generatePerformanceDates(input: $input) {
        clientMutationId
        sheet {
          performanceDatesDescription
        }
      }
    }
  }
`;

export const useOfferSheetStore = defineStore('offerSheet', {
  state: (): OfferSheetState => ({
    offerSheet: undefined,
    offerSheetLoading: true,
    templateLoading: false,
    agentLoading: false,
    contactsLoading: false,
    expensesLoading: false,
    listLoading: false,
    ticketsLoading: false,
    summaryReloading: false,
    templates: [],
  }),

  actions: {
    async fetchOfferSheet(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.offerSheetLoading = true;
      this.offerSheet = undefined;

      try {
        const { data } = await apiClient.query<Query>({
          query: OFFER_SHEET_QUERY,
          variables: { id },
          fetchPolicy: 'network-only',
        });
        this.offerSheet = JSON.parse(JSON.stringify(data.offerSheet));
      } finally {
        this.offerSheetLoading = false;
      }
    },

    async loadTemplates() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const { data } = await apiClient.query<OfferSheetTemplate[]>({
        query: OFFER_SHEET_TEMPLATES_QUERY,
      });
      this.templates = data.templates;
    },

    async reloadSummary() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      if (!this.offerSheet || this.summaryReloading) {
        return;
      }
      this.summaryReloading = true;

      try {
        const { data } = await apiClient.query<Query>({
          query: OFFER_SHEET_SUMMARY_QUERY,
          variables: { id: this.offerSheet?.id },
          fetchPolicy: 'no-cache',
        });
        this.offerSheet.summary = { ...data.offerSheet?.summary };
        this.offerSheet.htmlContent = data.offerSheet?.htmlContent;
      } finally {
        this.summaryReloading = false;
      }
    },

    async updateOfferSheet(payload: UpdateSheetInput, nullableFields: string[] = []) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      payload = ensureNumbers(payload, ...nullableFields);
      this.offerSheetLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: OFFER_SHEET_MUTATION,
          variables: { input: payload },
        });
        if (this.offerSheet) {
          this.offerSheet = Object.assign({ ...this.offerSheet }, data?.offerSheet?.updateSheet?.sheet);
        }
      } finally {
        this.offerSheetLoading = false;
        this.reloadSummary();
      }
    },

    async createVersion(id: number, name?: string) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      const { data } = await apiClient.mutate<Mutation>({
        mutation: CREATE_VERSION_MUTATION,
        variables: {
          input: {
            id,
            name,
          },
        },
        fetchPolicy: 'network-only',
      });

      return JSON.parse(JSON.stringify(data?.offerSheet?.createVersion?.version)) as SheetVersion;
    },

    async createTemplate(payload: CreateTemplateInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.templateLoading = true;

      try {
        await apiClient.mutate<Mutation>({
          mutation: CREATE_TEMPLATE_MUTATION,
          variables: {
            input: payload,
          },
        });
      } finally {
        this.templateLoading = false;
      }
    },

    async applyTemplate(templateId: number) {
      const payload: ApplyTemplateInput = { templateId, offerSheetId: this.offerSheet.id };
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.templateLoading = true;

      try {
        await apiClient.mutate<Mutation>({
          mutation: APPLY_TEMPLATE_MUTATION,
          variables: {
            input: payload,
          },
        });
      } finally {
        this.templateLoading = false;
      }
    },

    async lockSheet(payload: LockSheetInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      this.offerSheetLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: LOCK_SHEET_MUTATION,
          variables: {
            input: payload,
          },
        });

        this.offerSheet.pdfDownloadUrl = data?.offerSheet?.lockSheet?.sheet?.pdfDownloadUrl;
        this.offerSheet.lockedAt = data?.offerSheet?.lockSheet?.sheet?.lockedAt;
        this.offerSheet.lockedBy = User.fromGql(data?.offerSheet?.lockSheet?.sheet?.lockedBy);

        return data?.offerSheet?.lockSheet?.sheet?.pdfDownloadUrl;
      } finally {
        this.offerSheetLoading = false;
      }
    },

    async generatePerformanceDates(payload: GeneratePerformanceDatesInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      this.offerSheetLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: GENERATE_PERFORMANCE_DATES_MUTATION,
          variables: {
            input: payload,
          },
        });

        const performanceDatesDescription =
          data?.offerSheet?.generatePerformanceDates?.sheet?.performanceDatesDescription;

        this.offerSheet.performanceDatesDescription = performanceDatesDescription;
        return performanceDatesDescription;
      } finally {
        this.offerSheetLoading = false;
        this.reloadSummary();
      }
    },

    async upsertAgent(payload: UpsertAgentInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.agentLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: AGENT_MUTATION,
          variables: {
            input: payload,
          },
        });
        if (this.offerSheet) {
          this.offerSheet.agent = <Agent>data?.offerSheet?.upsertAgent?.sheet?.agent;
        }
      } finally {
        this.agentLoading = false;
        this.reloadSummary();
      }
    },

    async upsertOfferContact(payload: UpsertOfferContactPayload) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.contactsLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: CONTACT_MUTATION,
          variables: {
            input: payload,
          },
        });
        if (this.offerSheet) {
          this.offerSheet.contacts = <Contact[]>data?.offerSheet?.upsertOfferContact?.contacts;
        }
      } finally {
        this.contactsLoading = false;
        this.reloadSummary();
      }
    },

    async upsertExpenseCategory(payload: UpsertExpenseCategoryInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.expensesLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: EXPENSE_CATEGORY_MUTATION,
          variables: {
            input: payload,
          },
        });
        if (this.offerSheet) {
          this.offerSheet.expenseCategories = <ExpenseCategory[]>(
            data?.offerSheet?.upsertExpenseCategory?.sheet?.expenseCategories
          );
          return data?.offerSheet?.upsertExpenseCategory?.expenseCategory;
        }
      } finally {
        this.expensesLoading = false;
        this.reloadSummary();
      }
    },

    async upsertExpense(payload: UpsertExpenseInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.expensesLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: EXPENSE_MUTATION,
          variables: {
            input: payload,
          },
        });
        if (!this.offerSheet) {
          return;
        }
        const category = data?.offerSheet?.upsertExpense?.sheet?.expenseCategories?.find(
          ({ id }) => payload.expenseCategoryId == id,
        );
        if (!category) {
          return;
        }
        const existing = this.offerSheet.expenseCategories.find(({ id }) => category.id == id);
        if (!existing) {
          return;
        }
        existing.expenses = <Expense[]>category.expenses;
        return data?.offerSheet?.upsertExpense?.expense;
      } finally {
        this.expensesLoading = false;
        this.reloadSummary();
      }
    },

    async upsertList(payload: UpsertListInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.listLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: LIST_MUTATION,
          variables: {
            input: payload,
          },
        });
        if (this.offerSheet) {
          this.offerSheet.lists = <List[]>data?.offerSheet?.upsertList?.sheet?.lists;
          return data?.offerSheet?.upsertList?.list;
        }
      } finally {
        this.listLoading = false;
        this.reloadSummary();
      }
    },

    async upsertTicket(payload: UpsertTicketInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.ticketsLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: TICKET_MUTATION,
          variables: {
            input: payload,
          },
        });
        if (this.offerSheet) {
          this.offerSheet.tickets = (data?.offerSheet?.upsertTicket?.sheet?.tickets || []).map(
            OfferSheetTicket.fromGql,
          );
          return data?.offerSheet?.upsertTicket?.ticket;
        }
      } finally {
        this.ticketsLoading = false;
        this.reloadSummary();
      }
    },

    async deleteTicket(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.ticketsLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: DELETE_TICKET_MUTATION,
          variables: {
            input: { id },
          },
        });
        if (this.offerSheet) {
          this.offerSheet.tickets = (data?.offerSheet?.upsertTicket?.sheet?.tickets || []).map(
            OfferSheetTicket.fromGql,
          );
        }
      } finally {
        this.ticketsLoading = false;
        this.reloadSummary();
      }
    },

    async deleteList(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.listLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: DELETE_LIST_MUTATION,
          variables: {
            input: { id },
          },
        });
        if (this.offerSheet) {
          this.offerSheet.lists = <List[]>data?.offerSheet?.deleteList?.sheet?.lists;
        }
      } finally {
        this.listLoading = false;
        this.reloadSummary();
      }
    },

    async deleteOfferContact(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.contactsLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: DELETE_CONTACT_MUTATION,
          variables: {
            input: { id },
          },
        });
        if (this.offerSheet) {
          this.offerSheet.contacts = <Contact[]>data?.offerSheet?.deleteOfferContact?.contacts;
        }
      } finally {
        this.contactsLoading = false;
        this.reloadSummary();
      }
    },

    async deleteExpenseCategory(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.expensesLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: DELETE_EXPENSE_CATEGORY_MUTATION,
          variables: {
            input: { id },
          },
        });
        if (this.offerSheet) {
          this.offerSheet.expenseCategories = <ExpenseCategory[]>(
            data?.offerSheet?.deleteExpenseCategory?.sheet?.expenseCategories
          );
        }
      } finally {
        this.expensesLoading = false;
        this.reloadSummary();
      }
    },

    async deleteExpense(id: number, categoryId: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.expensesLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: DELETE_EXPENSE_MUTATION,
          variables: {
            input: { id },
          },
        });
        if (!this.offerSheet) {
          return;
        }
        const category = data?.offerSheet?.deleteExpense?.sheet?.expenseCategories?.find(({ id }) => categoryId == id);
        if (!category) {
          return;
        }
        const existing = this.offerSheet.expenseCategories.find(({ id }) => category.id == id);
        if (!existing) {
          return;
        }
        existing.expenses = <Expense[]>category.expenses;
      } finally {
        this.expensesLoading = false;
        this.reloadSummary();
      }
    },
  },
});
